import { FeatureParityIcon, HonestPricingIcon, NoLockInIcon, TelemetryPatternIcon, ZeroConfigIcon } from "../icons-v2";
import type {
  ComparisonSection,
  ComparisonTableRow,
  SwitchStatsSection,
} from "./comparison-types";

export type {
  ComparisonColumnContent,
  ComparisonSection,
  ComparisonTableCell,
  ComparisonTableRow,
  PricingRow,
  SwitchStatItem,
  SwitchStatsSection,
} from "./comparison-types";

export const CORALOGIX_COMPARISON_SECTIONS: ComparisonSection[] = [
  {
    id: "full-feature-parity",
    title: "Full Feature Parity",
    middleware: {
      description:
        "APM, infrastructure, logs, distributed tracing, RUM, synthetics, databases, containers, Kubernetes, serverless, browser testing, and LLM observability all bundled in one platform. Every signal is searchable, correlated, and priced consistently.",
      bullets: [
        "Every observability signal included in one platform",
        "Unified timeline across logs, metrics, traces",
        "LLM observability and Session Replay included",
        "One pricing model across every telemetry type",
      ],
      image: "/images/comparison/feature-parity-middleware.webp",
    },
    competitor: {
      description:
        "APM, infrastructure, logs, distributed tracing, RUM, and security monitoring are available, but telemetry is routed through the proprietary Streama engine and managed across Frequent Search, Monitoring, and Compliance tiers.",
      bullets: [
        "Routed through Streama's proprietary architecture",
        "Data assigned to Frequent Search & Monitoring",
        "Routing decisions affect search, retention & pricing",
        "Additional features follow the same tier-based model",
      ],
      image: "/images/comparison/full-feature-parity-coralogix.webp",
    },
    icon: FeatureParityIcon,
  },
  {
    id: "otel-not-streama",
    title: "OpenTelemetry-native, not Streama-native",
    middleware: {
      description:
        "Built on OpenTelemetry from ingestion through visualization. Keep your existing instrumentation, collectors, and pipelines without translating telemetry into a proprietary backend or data model.",
      bullets: [
        "100% OpenTelemetry-native architecture",
        "Repoint existing OTel Collectors without code changes",
        "Cloud, BYOC, hybrid, and on-prem deployments",
        "Open dashboards and PromQL-based alerting",
      ],
      image: "/images/comparison/no-lock-in-middleware.webp",
    },
    competitor: {
      description:
        "Coralogix supports OpenTelemetry ingestion, but converts incoming telemetry into its proprietary Streama engine. Dashboards, alerting, and pipelines become tightly coupled to the platform.",
      bullets: [
        "OTel data converted into the Streama data model",
        "Proprietary dashboards and alert definitions",
        "Migration requires rebuilding platform-specific assets",
        "No on-prem deployment option",
      ],
    },
    icon: NoLockInIcon,
  },
  {
    id: "cost-control",
    title: "Cost control without the policy treadmill",
    middleware: {
      description:
        "Pay for the data you send with predictable pricing and simple controls to reduce telemetry volume. No routing rules, priority policies, or ongoing optimization projects required.",
      bullets: [
        "Predictable pricing based on data ingested",
        "Two-click Ingestion Control for noisy telemetry",
        "180-day retention included",
        "No per-host, per-user, or per-container fees",
      ],
    },
    competitor: {
      description:
        "Coralogix provides flexible cost optimization through TCO Optimizer, but controlling spend requires maintaining routing policies, retention rules, and priority assignments over time.",
      bullets: [
        "Per-GB pricing split across multiple priority tiers",
        "TCO Optimizer policies require ongoing maintenance",
        "Unmatched data defaults to higher-cost storage tiers",
        "Additional archive and cloud storage costs may apply",
      ],
    },
    icon: HonestPricingIcon,
  },
  {
    id: "unified-observability",
    title: "Unified observability, not logs-first",
    middleware: {
      description:
        "Logs, metrics, traces, infrastructure, and events share one timeline and one correlation layer. Investigate issues from any signal without switching between products or manually connecting telemetry.",
      bullets: [
        "Unified timeline across every telemetry signal",
        "Automatic cross-signal correlation with OpenTelemetry",
        "Native logs-to-traces navigation",
        "LLM observability included at no extra cost",
      ],
    },
    competitor: {
      description:
        "Coralogix expanded from log analytics into full-stack observability, but many workflows still revolve around log pipelines, storage tiers, and manual correlation for custom telemetry.",
      bullets: [
        "Log-first architecture with tier-based pipelines",
        "Manual Correlation Mapping for custom fields",
        "LLM monitoring billed separately",
        "RUM follows the same pipeline model",
      ],
    },
    icon: TelemetryPatternIcon,
  },
  {
    id: "setup-in-minutes",
    title: "Setup in minutes, not weeks",
    middleware: {
      description:
        "Install one lightweight OpenTelemetry Collector and start monitoring in under a minute. No pipeline design, proprietary query language, or complex onboarding required.",
      bullets: [
        "One-script installation",
        "Automatic service and dependency discovery",
        "Visual query builder with PromQL support",
        "One-hour support SLA on every plan",
      ],
      image: "/images/comparison/zero-config-middleware.webp",
    },
    competitor: {
      description:
        "Getting full value from Coralogix typically means configuring Streama pipelines, learning DataPrime for advanced investigations, and maintaining TCO Optimizer policies over time.",
      bullets: [
        "Streama pipeline configuration required",
        "DataPrime needed for advanced investigations",
        "Ongoing TCO Optimizer policy management",
        "Premium support available only on Enterprise plans",
      ],
      image: "/images/comparison/setup-in-min-coralogix.webp",
    },
    icon: ZeroConfigIcon,
  },
];

export const CORALOGIX_FEATURE_TABLE_ROWS: ComparisonTableRow[] = [
  {
    id: "bottom-line",
    label: "Bottom line",
    middleware: {
      text: "Teams wanting unified, OpenTelemetry-native observability",
    },
    competitor: {
      text: "Teams invested in the Coralogix platform",
    },
  },
  {
    id: "recommended-for",
    label: "Recommended for",
    middleware: {
      text: "Cost-conscious, OTel-native cloud teams",
    },
    competitor: {
      text: "Teams willing to manage routing policies and Streama pipelines",
    },
  },
  {
    id: "otel-native",
    label: "OpenTelemetry-native",
    middleware: {
      status: "check",
      description: "Built on OpenTelemetry",
    },
    competitor: {
      status: "cross",
      description: "OTel ingest with proprietary Streama backend",
    },
  },
  {
    id: "byoc-on-prem",
    label: "BYOC / On-prem",
    middleware: {
      status: "check",
      description: "Cloud, BYOC & on-prem",
    },
    competitor: {
      status: "cross",
      description: "Limited (S3 archive only)",
    },
  },
  {
    id: "value-for-money",
    label: "Value for money",
    middleware: {
      status: "check",
      description: "2-click Ingestion Control",
    },
    competitor: {
      status: "cross",
      description: "Tier-based costs increase with routing complexity",
    },
  },
  {
    id: "pricing-model",
    label: "Pricing model",
    middleware: {
      status: "check",
      description: "Pay for data ingested",
    },
    competitor: {
      status: "cross",
      description: "Per-GB with Frequent Search, Monitoring & Compliance tiers",
    },
  },
  {
    id: "cost-control",
    label: "Cost control",
    middleware: {
      status: "check",
      description: "2-click Ingestion Control",
    },
    competitor: {
      status: "cross",
      description: "TCO Optimizer policies and routing rules",
    },
  },
  {
    id: "per-host-fees",
    label: "Per-host fees",
    middleware: {
      status: "check",
      description: "None",
    },
    competitor: {
      status: "check",
      description: "None",
    },
  },
  {
    id: "per-user-fees",
    label: "Per-user fees",
    middleware: {
      status: "check",
      description: "None",
    },
    competitor: {
      status: "check",
      description: "None",
    },
  },
  {
    id: "data-priority-tiers",
    label: "Data priority tiers",
    middleware: {
      status: "check",
      description: "None",
    },
    competitor: {
      description: "Frequent Search, Monitoring, Compliance",
    },
  },
  {
    id: "query-language",
    label: "Query language",
    middleware: {
      status: "check",
      description: "Visual UI, PromQL & SQL-like logs",
    },
    competitor: {
      description: "DataPrime & Lucene",
    },
  },
  {
    id: "unified-observability",
    label: "Unified observability",
    middleware: {
      status: "check",
      description: "One timeline across all signals",
    },
    competitor: {
      description: "Log-first architecture",
    },
  },
  {
    id: "logs-to-traces",
    label: "Logs-to-traces correlation",
    middleware: {
      status: "check",
      description: "Native via OpenTelemetry",
    },
    competitor: {
      status: "check",
      description: "Native via Coralogix agents",
    },
  },
  {
    id: "search-on-all-spans",
    label: "Search on all spans",
    middleware: {
      status: "check",
      description: "Search every stored span",
    },
    competitor: {
      description: "Depends on priority tier",
    },
  },
  {
    id: "llm-observability",
    label: "LLM observability",
    middleware: {
      status: "check",
      description: "Included",
    },
    competitor: {
      description: "Separate AI token pricing",
    },
  },
  {
    id: "rum-session-replay",
    label: "RUM & Session Replay",
    middleware: {
      status: "check",
      description: "Included",
    },
    competitor: {
      description: "Available through the same pipeline model",
    },
  },
  {
    id: "data-retention",
    label: "Data retention",
    middleware: {
      status: "check",
      description: "180 days included",
    },
    competitor: {
      description: "Tier-dependent + S3 Remote Query",
    },
  },
  {
    id: "dedicated-support",
    label: "Dedicated technical support",
    middleware: {
      headline: "All plans",
      headlineStyle: "bold",
      description: "Real support, even on the entry tier.",
    },
    competitor: {
      headline: "Enterprise only",
      headlineStyle: "bold",
      description:
        "Premium SLAs and dedicated support reserved for Enterprise customers.",
    },
  },
];

export const CORALOGIX_SWITCH_STATS: SwitchStatsSection = {
  tag: "What customers get",
  title: "The numbers after the switch",
  description:
    "Averages across customers who migrated from Coralogix to Middleware.",
  stats: [
    {
      id: "troubleshooting-speed",
      value: "4",
      unit: "×",
      title: "Troubleshooting",
      description:
        "Faster mean-time-to-detect with unified signals on one timeline.",
    },
    {
      id: "cost-reduction",
      value: "50",
      unit: "%",
      title: "Cost Reduction",
      description:
        "Reduction in observability spend versus Coralogix tier-based contracts",
    },
    {
      id: "support-response",
      value: "1",
      unit: "hr",
      title: "Issue response SLA",
      description: "Critical-issue response SLA on every plan",
    },
    {
      id: "opsai-automation",
      value: "80",
      unit: "%",
      title: "Auto fixes with accuracy",
      description:
        "OpsAI resolves issues end-to-end, saving 60% of engineer time.",
    },
  ],
};
